import { useRef, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import { FaWater } from "react-icons/fa";
import { FaArrowUpRightFromSquare, FaArrowRight, FaFlag, FaShieldHalved, FaClipboardCheck, FaUsers } from "react-icons/fa6";
import { PiMountains } from "react-icons/pi";
import { BsTree } from "react-icons/bs";
import { VscCompass } from "react-icons/vsc";
import { Link } from "react-router-dom";
import "./ForgeEcosystem.css";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease, delay } },
});

/* ── Animated counter ─────────────────────────────────────── */
function useCounter(target, duration = 1300, active = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(2, -10 * p);
      setVal(Math.floor(eased * target));
      if (p < 1) requestAnimationFrame(step);
      else setVal(target);
    };
    requestAnimationFrame(step);
  }, [active, target, duration]);
  return val;
}

const RAIL_ICONS = [PiMountains, BsTree, FaWater, VscCompass];

/* ── Clinical research partners — linked, real URLs ── */
const RESEARCH_PARTNERS = [
  {
    id: "cfpr",
    name: "The Centre for Psychedelic Research",
    logo: "./assets/research/cfpr.png",
    url: "https://www.imperial.ac.uk/a-z-research/psychedelic-research-centre/",
    blurb: "Advancing evidence-based psychedelic research.",
  },
  {
    id: "jhu",
    name: "Johns Hopkins University",
    logo: "./assets/research/jhu.svg",
    url: "https://www.hopkinspsychedelic.org/",
    blurb: "Collaborating on mental health research and innovation.",
  },
  {
    id: "northwell",
    name: "Northwell Health",
    logo: "./assets/research/northwell.svg",
    url: "https://feinstein.northwell.edu/",
    blurb: "Partnering to expand access to quality behavioral healthcare.",
  },
  {
    id: "maps",
    name: "MAPS",
    logo: "./assets/research/maps.svg",
    url: "https://maps.org/",
    blurb: "Supporting responsible psychedelic education and research.",
  },
  {
    id: "hhp",
    name: "Heroic Hearts Project",
    logo: "./assets/research/heroic.png",
    url: "https://www.heroicheartsproject.org/",
    blurb: "Helping veterans and first responders heal and thrive.",
  },
];

/* ── Retreat & program partnerships ── */
const PROGRAM_PARTNERS = [
  {
    id: "cerro",
    name: "Cerro Tusa Springs",
    logo: "https://cerrotusasprings.com/wp-content/uploads/2023/03/Proyecto-nuevo-3-255x69.png",
    url: "https://cerrotusasprings.com/",
    blurb: "Our retreat home in Colombia, surrounded by nature and tradition.",
  },
  {
    id: "evermen",
    name: "Evermen",
    logo: "https://evermen.com/assets/icons/logodark.svg",
    url: "https://evermen.com/",
    blurb: "Strategic partner supporting men's health, education, and transformation.",
  },
  {
    id: "harmonica",
    name: "Harmonica",
    logo: "https://ayahuasca-retreat.com/wp-content/uploads/2024/07/harmonica-ayahuasca-retreat-colombia-medellin.png.webp",
    url: "https://ayahuasca-retreat.com/",
    blurb: "Our retreat partner in Colombia, offering transformative experiences.",
  },
];

/* ── Snapshot stats — icon + number + labels ── */
function NumStat({ Icon, target, suffix, labels, delay, active }) {
  const count = useCounter(target, 1300, active);
  return (
    <motion.div className="eco__stat" variants={fadeUp(delay)} initial="hidden" animate={active ? "show" : "hidden"}>
      <span className="eco__stat-icon">
        <Icon aria-hidden="true" />
      </span>
      <div>
        <div className="eco__stat-num">
          {count}
          {suffix}
        </div>
        <div className="eco__stat-labels">
          {labels.map((l, i) => (
            <span key={i}>{l}</span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function TextStat({ Icon, value, labels, delay, active, serif }) {
  return (
    <motion.div className="eco__stat" variants={fadeUp(delay)} initial="hidden" animate={active ? "show" : "hidden"}>
      <span className="eco__stat-icon">
        <Icon aria-hidden="true" />
      </span>
      <div>
        <div className={`eco__stat-num ${serif ? "eco__stat-num--serif" : "eco__stat-num--display"}`}>{value}</div>
        <div className="eco__stat-labels">
          {labels.map((l, i) => (
            <span key={i}>{l}</span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Linked partner card, used in both Research and Partnerships ── */
function PartnerCard({ p, delay, active, variant }) {
  return (
    <motion.a
      href={p.url}
      target="_blank"
      rel="noreferrer noopener"
      className={`eco__partner-card eco__partner-card--${variant}`}
      variants={fadeUp(delay)}
      initial="hidden"
      animate={active ? "show" : "hidden"}
    >
      <img src={p.logo} alt={p.name} className="eco__partner-logo" loading="lazy" style={{ filter: p.name == "Harmonica" ? "invert(1)" : "invert(0)" }} />
      {/* <p className="eco__partner-name">{p.name}</p> */}
      <p className="eco__partner-blurb d-block">{p.blurb}</p>
      <FaArrowUpRightFromSquare className="eco__partner-link-icon" aria-hidden="true" />
    </motion.a>
  );
}

/* ── Main ─────────────────────────────────────────────────── */
export default function ForgeEcosystem() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="eco" id="research" ref={ref}>
      <div className="eco__layout container-fg container-2xxl">
        <motion.aside className="hero__rail eco__rail" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : { opacity: 0 }} transition={{ duration: 1, delay: 0.4, ease }} aria-hidden="true">
          <span className="hero__rail-num">04</span>
          <span className="hero__rail-line" />
          <ul className="hero__rail-icons">
            {RAIL_ICONS.map((Icon, i) => (
              <li key={i}>
                <Icon />
                <span className={`hero__rail-line ${i === RAIL_ICONS.length - 1 ? "" : "alt"}`} />
              </li>
            ))}
          </ul>
        </motion.aside>

        <div className="eco__content">
          {/* ── Header: eyebrow + two-tone headline | intro copy ── */}
          <div className="eco__header">
            <motion.div variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
              <span className="eco__eyebrow u-eyebrow">The Forge Ecosystem</span>
              <h2 className="eco__headline">
                <span className="eco__hl-ink u-display">Stronger Together.</span>
                <br />
                <span className="eco__hl-rust u-display">Greater Impact.</span>
              </h2>
            </motion.div>
            <motion.p className="eco__header-copy" variants={fadeUp(0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
              Forge exists at the intersection of healing, research, and community. We collaborate with leading organizations and partners to create real, lasting change.
            </motion.p>
          </div>

          {/* ── Snapshot ── */}
          <motion.span className="eco__tag eco__tag--snapshot" variants={fadeUp(0.14)} initial="hidden" animate={inView ? "show" : "hidden"}>
            Snapshot
          </motion.span>
          <motion.div className="eco__panel eco__panel--snapshot" variants={fadeUp(0.16)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <NumStat Icon={FaFlag} target={2} suffix="+" labels={["RETREATS", "COMPLETED"]} delay={0.18} active={inView} />
            <TextStat Icon={FaShieldHalved} value="501(c)(3)" labels={["REGISTERED NONPROFIT", "SINCE DEC 2024"]} delay={0.22} active={inView} serif />
            <TextStat Icon={FaClipboardCheck} value="CLINICAL" labels={["ASSESSMENT TRACKING", "PCL-5, DASS, BDI, MADRS"]} delay={0.26} active={inView} />
            <NumStat Icon={FaUsers} target={47} suffix="+" labels={["LIVES TOUCHED", "ACROSS 12 STATES"]} delay={0.3} active={inView} />
          </motion.div>

          {/* ── Clinical Research & Programs ── */}
          <motion.span className="eco__tag eco__tag--research" variants={fadeUp(0.32)} initial="hidden" animate={inView ? "show" : "hidden"}>
            Clinical Research &amp; Programs
          </motion.span>
          <motion.div className="eco__panel eco__panel--research" variants={fadeUp(0.34)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <div className="eco__partner-row eco__partner-row--research">
              {RESEARCH_PARTNERS.map((p, i) => (
                <PartnerCard key={p.id} p={p} variant="research" delay={0.36 + i * 0.06} active={inView} />
              ))}
            </div>
          </motion.div>

          {/* ── Partnerships ── */}
          <motion.span className="eco__tag eco__tag--partnerships" variants={fadeUp(0.5)} initial="hidden" animate={inView ? "show" : "hidden"}>
            Partnerships
          </motion.span>
          <motion.div className="eco__panel eco__panel--partnerships" variants={fadeUp(0.52)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <div className="eco__partner-row eco__partner-row--partnerships">
              {PROGRAM_PARTNERS.map((p, i) => (
                <PartnerCard key={p.id} p={p} variant="partnerships" delay={0.54 + i * 0.08} active={inView} />
              ))}
            </div>
          </motion.div>

          {/* ── Closing CTA bar ── */}
          <motion.div className="eco__closing" variants={fadeUp(0.6)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <span className="eco__closing-icon">
              <FaFlag aria-hidden="true" />
            </span>
            <p className="eco__closing-copy">Together, we're building a future where healing is accessible, research drives solutions, and no one walks this path alone.</p>
            <Link to="/community" className="eco__closing-cta">
              Learn More About Our Impact <FaArrowRight aria-hidden="true" />
            </Link>
          </motion.div>
        </div>
      </div>
      <div className="wfe__divider d-flex justify-content-center align-items-center gap-2" aria-hidden="true">
        <span className="border-bottom-div" />
        <img src="./assets/mountainsForst.png" className="wfe__divider-img" alt="" />
        <span className="border-bottom-div" />
      </div>
    </section>
  );
}
